// Folklore Atlas: folktales as wisps of light over a globe that unrolls into a flat map.
// data/map.json and data/tales/<id>.json are generated by scripts/build-data.js from the
// trilogy Annotated Folktales; data/world.json and data/world-detail.json hold Natural Earth countries
// (scripts/build-world.js).

import { geoArea, geoBounds, geoClipAntimeridian, geoClipCircle, geoDistance, geoEquirectangularRaw, geoGraticule10, geoOrthographicRaw, geoPath, geoProjectionMutator } from 'd3-geo';
import { feature, mesh } from 'topojson-client';
import { drawBackdrop, drawConstellation, drawMap, drawWisps, layer, wispSprites } from './render.js';
import { MERCATOR_MAX, detailMap, flatView, mercatorLat } from './lib/detail-map.js';
import { indexTale, searchTales } from './lib/search.js';
import { distanceToRings, insideRings, random, rankBySpread, smoothstep, spreadOver, sunflower } from './lib/wisps.js';

(async function () {
  const $ = (sel) => document.querySelector(sel);
  const RAD = Math.PI / 180;
  const ZMAX = 6;
  // Wisps show when they are at least this many pixels from every brighter wisp.
  const WISP_SPACING = 34;
  const KIN_SHOWN = 10;
  const FAM_SHOWN = 8;
  const OTHERS_SHOWN = 4;
  const FONT_SIZES = [18, 20, 22, 25, 28];
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

  const clamp = (x, a, b) => Math.max(a, Math.min(b, x));
  const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
  const wrap = (d) => ((((d + 180) % 360) + 360) % 360) - 180;
  const el = (tag, cls, text) => {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  };

  const worldEl = $('#world');
  const panel = $('#panel');
  const loading = $('#loading');

  // A keyboard opening over the search box squeezes the window on some phones: while typing, a change of
  // height alone (not a turn of the phone) leaves the stars and the globe where they are.
  let laidOut = { W: innerWidth, H: innerHeight };
  const typing = () => document.activeElement?.matches('input, textarea') ?? false;
  const onResize = (fn) => () => {
    if (innerWidth === laidOut.W && innerHeight < laidOut.H && typing()) return;
    fn();
  };
  drawSky();
  window.addEventListener('resize', onResize(drawSky));
  // iOS scrolls the page to bring a focused field into view, and can leave it scrolled: put it back.
  document.addEventListener('focusout', () => requestAnimationFrame(() => (scrollX || scrollY) && !typing() && scrollTo(0, 0)));
  // Safari's pinch zoom outside the map (touch-action keeps it off elsewhere): the map zooms itself.
  document.addEventListener('gesturestart', (e) => e.preventDefault());

  const json = (url) =>
    fetch(url).then((r) => {
      if (!r.ok) throw new Error(`${url}: ${r.status}`);
      return r.json();
    });
  let data, world;
  try {
    [data, world] = await Promise.all([json('data/map.json'), json('data/world.json')]);
  } catch (err) {
    console.error(err);
    loading.textContent = 'The wisps could not be gathered. Try reloading.';
    return;
  }

  // ---------- Tales, places and their kin ----------

  const typeInfo = (atu) => {
    const [name = '', famIdx = -1] = data.types[atu] ?? [];
    const [family = '', kindIdx = -1] = data.families[famIdx] ?? [];
    return { typeName: name, famIdx, family, kindIdx, kind: data.kinds[kindIdx] ?? '' };
  };

  const places = data.places.map((p) => ({ ...p, tales: [] }));
  const placeById = new Map(places.map((p) => [p.id, p]));
  const tales = [];
  for (const [i, p] of data.places.entries()) {
    for (const [id, title, atu, excerpt] of p.tales) {
      const tale = { id, title, atu, excerpt, place: places[i], ...typeInfo(atu) };
      indexTale(tale, tale.typeName, `atu ${atu}`, p.name, tale.family, tale.kind, excerpt);
      places[i].tales.push(tale);
      tales.push(tale);
    }
  }
  const taleById = new Map(tales.map((t) => [t.id, t]));

  // key -> place -> tales of that key there
  const group = (keyOf) => {
    const map = new Map();
    for (const t of tales) {
      const key = keyOf(t);
      if (!map.has(key)) map.set(key, new Map());
      const byPlace = map.get(key);
      if (!byPlace.has(t.place)) byPlace.set(t.place, []);
      byPlace.get(t.place).push(t);
    }
    return map;
  };
  const byType = group((t) => t.atu);
  const byFamily = group((t) => t.famIdx);
  // The other places that tell the same tale (by type) or a tale of the same family, most tellings first.
  // Places in skip (those that tell the same tale) are left out of the same family.
  const kinOf = (tale, by, skip = []) => {
    const byPlace = (by === 'type' ? byType.get(tale.atu) : byFamily.get(tale.famIdx)) ?? new Map();
    const skipped = new Set(skip.map((k) => k.place));
    return [...byPlace]
      .filter(([place]) => place !== tale.place && !skipped.has(place))
      .sort((a, b) => b[1].length - a[1].length || a[0].name.localeCompare(b[0].name))
      .map(([place, list]) => ({ place, tales: list }));
  };

  // ---------- The map ----------

  const countries = feature(world, world.objects.countries).features;
  const land = feature(world, world.objects.land);
  // Borders and coasts as meshes: every shared border is projected and drawn once, not once per country.
  const borders = mesh(world, world.objects.countries, (a, b) => a !== b);
  const coast = mesh(world, world.objects.land);
  const graticule = geoGraticule10();
  // The 1:50m outlines, loaded after the first view and drawn on the flat map from this zoom on
  // (lib/detail-map.js); the globe and the unrolling keep the 1:110m ones.
  const DETAIL_Z = 1.25;
  let detail = null;
  const countryByName = new Map(countries.map((f) => [f.properties.name, f]));
  const backLayer = layer($('#backdrop'));
  const mapLayer = layer($('#map'));
  const wispLayer = layer($('#sky-wisps'), 1);
  let sprites = null;

  // Zoomed into the flat map, it turns into Mercator, as web maps do: conformal, so every country on
  // screen keeps its true shape, and a pan only moves the map, never reshapes it. m blends a latitude
  // (m = 0) into its Mercator y (m = 1), in degrees, from the flat map (z = 1) to z = 2.5.
  const mercatorAt = (z) => ease(clamp((z - 1) / 1.5, 0, 1));
  const yOf = (lat, m) => (m ? (1 - m) * lat + m * mercatorLat(lat) : lat);
  // The latitude at a y: y grows with the latitude, so halve the range until it is found.
  function latOf(y, m) {
    if (!m) return clamp(y, -90, 90);
    let lo = -90;
    let hi = 90;
    for (let i = 0; i < 40; i++) {
      const mid = (lo + hi) / 2;
      if (yOf(mid, m) < y) lo = mid;
      else hi = mid;
    }
    return (lo + hi) / 2;
  }
  const MERC_MAX = MERCATOR_MAX * RAD;
  const mercatorRaw = (y) => Math.log(Math.tan(Math.PI / 4 + clamp(y, -MERC_MAX, MERC_MAX) / 2));

  // Blends an orthographic globe (a = 0) into an equirectangular map (a = 1), and that into Mercator (m).
  const projectionAt = geoProjectionMutator((a, m) => (x, y) => {
    const p0 = geoOrthographicRaw(x, y);
    const p1 = geoEquirectangularRaw(x, y);
    const y1 = m ? (1 - m) * p1[1] + m * mercatorRaw(y) : p1[1];
    return [p0[0] + a * (p1[0] - p0[0]), p0[1] + a * (y1 - p0[1])];
  });

  // z: 0 is the globe, 1 the flat world map, above 1 zoomed into the map.
  const v = { z: 0, lon: 20, lat: 22 };
  let anim = null;
  let drag = null;
  let pinch = null;
  let idleSince = 0;
  const t0 = performance.now();

  // Where the globe sits: centred on the screen below the header and controls. A tale panel at the side
  // overlays the globe's right edge a little; a bottom sheet (on phones) pushes the globe up instead.
  let target = null;
  let lay = null;
  const sheetQuery = matchMedia('(max-width: 760px)');
  // Phones, and anything held upright, skip the flat map in its ring for one that fills the screen.
  const fillQuery = matchMedia('(max-width: 760px), (orientation: portrait)');
  function measure() {
    const W = innerWidth;
    const H = innerHeight;
    let y0 = Math.max(56, $('.top').getBoundingClientRect().bottom - 8);
    let y1 = H;
    const controls = $('.controls').getBoundingClientRect();
    if (controls.top < H * 0.3) y0 = Math.max(y0, controls.bottom + 16);
    const sheet = sheetQuery.matches;
    // (Its height, not where it is: it rises into place, and slides as it is pulled. Pulled up past its
    // usual height, it covers the globe rather than squeezing it.)
    if (sheet && !panel.hidden) y1 = H - Math.min(panel.offsetHeight, H * 0.46);
    // On a phone the ring (1.18 globe radii, plus 18px and its lettering) nearly touches the screen's edges.
    const fit = (top, bottom) => (sheet ? Math.min((W - 44) / 661, (bottom - top - 92) / 661) : Math.min(W / 1056, (bottom - top) / 828));
    let k = fit(y0, y1);
    let cy = (y0 + y1) / 2;
    // A phone's globe keeps its size with the sheet open, rather than shrinking to fit the strip left above
    // it: it rises until its ring (330 px a unit of scale, and 46 px of lettering) meets the controls, and
    // its foot goes under the sheet. Only where that would take the chosen tale, at its centre, too near the
    // sheet does it shrink, and only as far as it must.
    if (sheet) {
      const cyMax = y1 - Math.max(90, 0.3 * (y1 - y0));
      k = Math.min(fit(y0, H), (cyMax - y0 - 46) / 330.4);
      cy = Math.min(cyMax, Math.max(cy, y0 + 330.4 * k + 46));
    } else {
      // Keep the globe from reaching more than 40% of the way under the side panel, open or not.
      const css = getComputedStyle(document.documentElement);
      const panelW = parseFloat(css.getPropertyValue('--panel-w'));
      const panelLeft = W - parseFloat(css.getPropertyValue('--gutter')) - panelW;
      k = Math.min(k, (panelLeft + 0.4 * panelW - W / 2) / 280);
    }
    // (Smaller on a phone than elsewhere, on a screen too short for more.)
    k = clamp(k, sheet ? 0.15 : 0.25, 1.8);
    // A phone skips the flat map in its ring for one that fills the screen: its scale needn't make room
    // for the sheet, so zooming in and out keeps the same steps with the sheet open or closed.
    const kf = sheet ? clamp(fit(y0, H), 0.25, 1.8) : k;
    const cx = W / 2;
    target = { W, H, cx, cy, sg: 280 * k, sf: 160 * kf, shift: (W / 2 - cx) * 0.58 };
    if (!lay) lay = { ...target };
    backLayer.resize(W, H);
    mapLayer.resize(W, H);
    if (wispLayer.resize(W, H) || !sprites) sprites = wispSprites(wispLayer.dpr);
    mapDirty = true;
  }
  function stepLayout() {
    let changed = false;
    for (const key of ['cx', 'cy', 'sg', 'sf', 'shift']) {
      const d = target[key] - lay[key];
      if (d === 0) continue;
      lay[key] = Math.abs(d) < 0.3 ? target[key] : lay[key] + d * 0.18;
      changed = true;
    }
    lay.W = target.W;
    lay.H = target.H;
    return changed;
  }

  // How far from the centre the half-unrolled globe shows. Past acos(-a / (1 - a)) the blend folds
  // back on itself and the far side would be drawn over the near side, so never show that far.
  const clipAngle = (a) => Math.min(90 + 89.5 * a, a < 0.5 ? Math.acos(-a / (1 - a)) / RAD : 180);

  function view() {
    const z = clamp(v.z, 0, ZMAX);
    // Once the ring has faded the map fills the screen, poles to the top and bottom edge, however far out.
    const cover = Math.max(lay.sf, lay.H / Math.PI, lay.W / (2 * Math.PI));
    const fadeAt = (z) => ease(clamp((z - 1) / 0.7, 0, 1));
    const scaleAt = (z, a) => (z <= 1 ? lay.sg + (lay.sf - lay.sg) * a : Math.max(lay.sf * Math.pow(2, z - 1), lay.sf + (cover - lay.sf) * fadeAt(z)));
    let a = ease(clamp(z, 0, 1));
    let fade = fadeAt(z);
    let s = scaleAt(z, a);
    let m = mercatorAt(z);
    // There the flat map is past the one in the ring, so the globe unrolls straight into it,
    // the scale, the fading ring and the turn into Mercator all keeping pace with the unrolling.
    const flat = flatZ();
    if (fillQuery.matches && z < flat) {
      a = ease(z / flat);
      fade = fadeAt(flat) * a;
      s = lay.sg + (scaleAt(flat, 1) - lay.sg) * a;
      m = mercatorAt(flat) * a;
    }
    // Zoomed in, keep the map's edges beyond the edges of the screen (or, while it is shorter than the
    // screen, inside it). Nearer the flat map, draw it in to the centre of the ring.
    // All in y (degrees, see yOf), the y of the map's top edge less the screen above and below the centre.
    const top = yOf(90, m);
    const maxN = top - lay.cy / s / RAD;
    const maxS = top - (lay.H - lay.cy) / s / RAD;
    const y = yOf(v.lat, m);
    const yF = clamp(y, Math.min(-maxS, maxN) * fade, Math.max(-maxS, maxN) * fade);
    const latF = yF === y ? v.lat : latOf(yF, m);
    return {
      z,
      a,
      s,
      m,
      yF,
      fade,
      cx: lay.cx + lay.shift * fade,
      cy: lay.cy,
      lon: v.lon,
      latF,
      rotLat: clamp(v.lat, -70, 70) * (1 - a),
      clip: a < 0.999 ? clipAngle(a) : null,
    };
  }

  // Past a hemisphere the clip circle alone doesn't cut the map at the antimeridian, so whatever
  // wraps around behind it would be drawn straight across the map: cut there too.
  const clipBoth = (angle) => {
    const circle = geoClipCircle(angle * RAD);
    return (sink) => circle(geoClipAntimeridian(sink));
  };

  function project(vw) {
    const p = projectionAt(vw.a, vw.m);
    p.rotate([-vw.lon, -vw.rotLat])
      .scale(vw.s)
      .translate([vw.cx, vw.cy + vw.yF * RAD * vw.s * vw.a])
      .preclip(vw.clip === null ? geoClipAntimeridian : vw.clip <= 90 ? geoClipCircle(vw.clip * RAD) : clipBoth(vw.clip))
      .precision(0.6);
    return p;
  }

  // On the flat map zoomed in, the projection as a scale and shift of degrees (and of y, see yOf), for the
  // detailed outlines; null elsewhere, and while the map is still wider than the screen.
  function flatAt(vw) {
    if (!detail || vw.z < DETAIL_Z) return null;
    const k = vw.s * RAD;
    if (lay.W / k > 300) return null;
    return flatView({ ox: vw.cx - k * vw.lon, oy: vw.cy + vw.yF * k, k, m: vw.m, w: lay.W, h: lay.H });
  }

  // The astrolabe ring around the globe (drawn in render.js); it grows away as the map unrolls.
  function frame(vw) {
    const m = 1 + 3 * vw.fade;
    return {
      cx: vw.cx,
      cy: vw.cy,
      rx: vw.s * (1.18 + (2.95 - 1.18) * vw.a) * m,
      // Flat, the ring meets the map's top and bottom edges (half its height is pi / 2).
      ry: vw.s * (1.18 + (1.56 - 1.18) * vw.a) * m,
      op: 1 - vw.fade,
    };
  }

  // How visible a point is: fades out near the globe's edge.
  const visibility = (vw, lonLat) =>
    vw.clip === null ? 1 : clamp((vw.clip - geoDistance(lonLat, [vw.lon, vw.rotLat]) / RAD) / 5, 0, 1);

  // Constellation: arcs from the chosen tale to a kindred tale in every place that has one, gold to
  // the same tale and silver to the same family, kept as Path2Ds until the view or the choice changes.
  function constellation(vw, p) {
    const from = [state.sel.lon, state.sel.lat];
    const arcsTo = (kin) => {
      const path2d = new Path2D();
      const path = geoPath(p, path2d);
      const ends = [];
      for (const k of kin) {
        const to = [k.tales[0].lon, k.tales[0].lat];
        path({ type: 'LineString', coordinates: [from, to] });
        const alpha = visibility(vw, to);
        const xy = alpha > 0 ? p(to) : null;
        if (xy) ends.push({ x: xy[0], y: xy[1], alpha });
      }
      return { path: path2d, ends };
    };
    return { gold: arcsTo(state.kin), silver: arcsTo(state.fam) };
  }

  // ---------- Wisps: every tale is one, shown as zoom makes room ----------

  // Each tale's spot: a country's tales are spread evenly over its whole area, and each of its places takes
  // the spots nearest it (the closest place-spot pairs pair up first), its most widely told tale nearest the
  // place. Places outside any country keep a spiral around themselves. A wisp drifts no farther than its
  // `room`, so it never leaves the country.
  const ringsOf = (f) => (f.geometry.type === 'Polygon' ? f.geometry.coordinates : f.geometry.coordinates.flat());
  const placesIn = new Map(); // country name -> its places
  for (const place of places) {
    place.tales.sort((a, b) => byType.get(b.atu).size - byType.get(a.atu).size || a.id - b.id);
    if (!placesIn.has(place.country)) placesIn.set(place.country, []);
    placesIn.get(place.country).push(place);
  }
  for (const [name, here] of placesIn) {
    const country = countryByName.get(name);
    const rings = country && ringsOf(country);
    const count = here.reduce((n, place) => n + place.tales.length, 0);
    const fits = (lon, lat, step) => insideRings(rings, [lon, lat]) && distanceToRings(rings, [lon, lat]) > step * 0.35;
    const parts = country && (country.geometry.type === 'Polygon' ? [country.geometry.coordinates] : country.geometry.coordinates);
    const boxes = parts?.map((coordinates) => geoBounds({ type: 'Polygon', coordinates }));
    const spots = country && spreadOver(count, boxes, geoArea(country) / RAD ** 2, fits, count);
    const spotsOf = new Map(here.map((place) => [place, []]));
    if (spots) {
      const pairs = here.flatMap((place) => spots.map((spot, i) => [geoDistance([place.lng, place.lat], spot), place, i]));
      pairs.sort((a, b) => a[0] - b[0]);
      const free = new Set(spots.keys());
      for (const [, place, i] of pairs) {
        const mine = spotsOf.get(place);
        if (!free.has(i) || mine.length === place.tales.length) continue;
        free.delete(i);
        mine.push(spots[i]); // nearest the place first
      }
    } else {
      for (const place of here) {
        const spiral = sunflower(place.tales.length, place.lat, country ? 0.15 : 0.4);
        spotsOf.set(place, spiral.map(([dLon, dLat]) => [place.lng + dLon, place.lat + dLat]));
      }
    }
    for (const [place, mine] of spotsOf) {
      mine.forEach(([lon, lat], i) => {
        const tale = place.tales[i];
        [tale.lon, tale.lat] = [lon, lat];
        tale.room = rings ? distanceToRings(rings, [lon, lat]) : Infinity;
      });
    }
  }
  const { spacing } = rankBySpread(
    tales.map((t) => [t.lon, t.lat]),
    7,
  );
  for (const [i, tale] of tales.entries()) {
    const rnd = random(tale.id * 7919);
    tale.spacing = spacing[i];
    // Drift a few pixels around its spot, and come and go slowly where the zoom is about to show or hide it.
    tale.drift = { r: 2.5 + rnd() * 3, f: [0, 0, 0, 0].map(() => 0.12 + rnd() * 0.25), ph: [0, 0, 0, 0].map(() => rnd() * Math.PI * 2) };
    tale.tide = { f: (2 * Math.PI) / (25 + rnd() * 35), ph: rnd() * Math.PI * 2 };
  }

  // The chosen tale and its kin always show: tale -> 'on' | 'kin' (same tale) | 'sib' (same family)
  let forced = new Map();
  const wispState = (tale) => forced.get(tale) ?? '';
  for (const tale of tales) {
    tale.dur = 4 + ((tale.id * 1.37) % 3.5);
    tale.delay = (tale.id * 1.91) % 7;
  }

  // The wisps on screen this frame: where they are and how bright, for drawing and for pointing at.
  function placeWisps(vw, p, fr, t) {
    const shown = [];
    const minSpacing = WISP_SPACING / vw.s; // radians on the ground
    for (const tale of tales) {
      let alpha = 1;
      if (!forced.has(tale)) {
        const tide = 1 + 0.15 * Math.sin(tale.tide.f * t + tale.tide.ph);
        alpha = smoothstep((tale.spacing / (minSpacing * tide) - 1) / 0.6);
        if (alpha <= 0.01) continue;
      }
      const { r, f, ph } = tale.drift;
      const k = Math.min(r / (vw.s * RAD), tale.room * 0.8); // pixels -> degrees, within the country
      const cl = Math.max(0.3, Math.cos(tale.lat * RAD));
      const lon = tale.lon + (k * (0.65 * Math.sin(f[0] * t + ph[0]) + 0.35 * Math.sin(f[1] * 1.7 * t + ph[1]))) / cl;
      const lat = tale.lat + k * (0.65 * Math.sin(f[2] * t + ph[2]) + 0.35 * Math.sin(f[3] * 1.7 * t + ph[3]));
      let vis = visibility(vw, [lon, lat]);
      if (vis <= 0) continue;
      const xy = p([lon, lat]);
      if (!xy || xy[0] < -30 || xy[0] > lay.W + 30 || xy[1] < -30 || xy[1] > lay.H + 30) continue;
      const ex = (xy[0] - fr.cx) / fr.rx;
      const ey = (xy[1] - fr.cy) / fr.ry;
      vis = Math.min(vis, clamp((1 - ex * ex - ey * ey) / 0.06, 0, 1));
      if (vis * alpha <= 0.01) continue;
      shown.push({ tale, x: xy[0], y: xy[1], alpha: vis * alpha, state: wispState(tale), dur: tale.dur, delay: tale.delay });
    }
    return shown;
  }

  // The wisp nearest a point on screen, if one is close enough to point at.
  let onScreen = [];
  function wispAt(x, y, reach = 20) {
    let best = null;
    let bestD = reach * reach;
    for (const w of onScreen) {
      const d = (w.x - x) ** 2 + (w.y - y) ** 2;
      if (d < bestD) (best = w), (bestD = d);
    }
    return best;
  }

  // For keyboards and screen readers: a button for every wisp on screen, in a visually hidden list.
  const wispList = $('#wisps');
  const wispButtons = new Map();
  let listKey = '';
  let listAt = 0;
  let focused = null;
  function updateWispList(now) {
    if (now - listAt < 300) return;
    listAt = now;
    for (const w of onScreen) {
      const b = wispButtons.get(w.tale);
      if (b) (b.dataset.x = w.x.toFixed(0)), (b.dataset.y = w.y.toFixed(0));
    }
    const key = onScreen.map((w) => w.tale.id).join();
    if (key === listKey) return;
    listKey = key;
    const keep = new Set(onScreen.map((w) => w.tale));
    for (const [tale, b] of wispButtons) {
      if (keep.has(tale) || b === document.activeElement) continue;
      b.remove();
      wispButtons.delete(tale);
    }
    for (const { tale } of onScreen) {
      if (wispButtons.has(tale)) continue;
      const b = el('button', 'wisp');
      b.type = 'button';
      b.setAttribute('aria-label', `${tale.title}, ${tale.place.name}`);
      b.addEventListener('click', () => pick(tale.id));
      b.addEventListener('focus', () => (focused = tale));
      b.addEventListener('blur', () => focused === tale && (focused = null));
      wispList.append(b);
      wispButtons.set(tale, b);
    }
    for (const w of onScreen) Object.assign(wispButtons.get(w.tale).dataset, { x: w.x.toFixed(0), y: w.y.toFixed(0) });
    markWisps();
  }
  function markWisps() {
    for (const [tale, b] of wispButtons) {
      for (const cls of ['on', 'kin', 'sib']) b.classList.toggle(cls, forced.get(tale) === cls);
    }
  }

  // Names under the chosen, the pointed-at and the focused wisp: a few elements, so the text stays sharp.
  const labelsEl = $('#labels');
  const labels = new Map(); // tale -> element
  function drawLabels() {
    const named = onScreen.filter((w) => w.tale === state.sel || w.tale === hovered || w.tale === focused);
    for (const [tale, node] of labels) {
      if (named.some((w) => w.tale === tale)) continue;
      node.remove();
      labels.delete(tale);
    }
    for (const w of named) {
      let node = labels.get(w.tale);
      if (!node) labelsEl.append((node = el('span', 'wl', w.tale.title))), labels.set(w.tale, node);
      node.style.transform = `translate(${w.x.toFixed(1)}px,${(w.y + 14).toFixed(1)}px) translateX(-50%)`;
      node.style.opacity = Math.min(1, w.alpha * 1.5).toFixed(2);
    }
  }

  // Country highlights fade in and out over half a second.
  const LEVELS = [['sib', 'toSib'], ['kin', 'toKin'], ['own', 'toOwn']];
  const highlights = new Map(); // feature -> { sib, kin, own, toSib, toKin, toOwn }
  function setHighlights(own, kin, sib) {
    for (const h of highlights.values()) h.toSib = h.toKin = h.toOwn = 0;
    // A country lights up once, in the brightest way it is kin.
    const lit = new Set();
    const mark = (name, key) => {
      const f = countryByName.get(name);
      if (!f || lit.has(f)) return;
      lit.add(f);
      if (!highlights.has(f)) highlights.set(f, { sib: 0, kin: 0, own: 0, toSib: 0, toKin: 0, toOwn: 0 });
      highlights.get(f)[key] = 1;
    };
    mark(own, 'toOwn');
    for (const name of kin) mark(name, 'toKin');
    for (const name of sib) mark(name, 'toSib');
    mapDirty = true;
  }
  function stepHighlights(dt) {
    let moving = false;
    const k = Math.min(1, dt / 500);
    for (const [f, h] of highlights) {
      for (const [key, to] of LEVELS) {
        const d = h[to] - h[key];
        if (d === 0) continue;
        h[key] = Math.abs(d) <= k ? h[to] : h[key] + Math.sign(d) * k;
        moving = true;
      }
      if (LEVELS.every(([key, to]) => !h[key] && !h[to])) highlights.delete(f);
    }
    return moving;
  }

  // ---------- Animation loop ----------

  // Without a z of its own, a flight keeps heading where the one before it was: never to a half-unrolled globe.
  function flyTo(to, dur = 900) {
    const t = { z: to.z ?? anim?.to.z ?? v.z, lon: to.lon ?? v.lon, lat: to.lat ?? v.lat };
    const morph = to.morph ?? (!!anim?.morph && t.z === anim.to.z);
    anim = { start: performance.now(), dur, from: { ...v }, to: t, dlon: wrap(t.lon - v.lon), morph };
  }

  function stepView(now, dt) {
    if (anim) {
      const k = clamp((now - anim.start) / anim.dur, 0, 1);
      const e = ease(k);
      v.z = anim.from.z + (anim.to.z - anim.from.z) * e;
      v.lon = wrap(anim.from.lon + anim.dlon * e);
      v.lat = anim.from.lat + (anim.to.lat - anim.from.lat) * e;
      if (k >= 1) anim = null;
    } else if (!state.sel && !state.list && !drag && !pinch && !reduceMotion && v.z < 0.4 && now - idleSince > 4000) {
      v.lon = wrap(v.lon + (3.5 * dt) / 1000);
    }
  }

  function clampLat() {
    if (v.z >= 1) v.lat = view().latF;
    else v.lat = clamp(v.lat, -70, 70);
  }

  let mapKey = '';
  let backdropKey = '';
  let mapDirty = true;
  let arcs = null;
  let arcsKey = '';
  let hovered = null;
  let controlsKey = '';
  let prev = performance.now();
  // While you turn, zoom or fly, draw at up to 60 frames a second (not 120 on a ProMotion screen);
  // while the map only turns and breathes on its own, 30 are enough and leave the machine idle.
  let activeUntil = 0;
  const keepBusy = (ms = 600) => (activeUntil = Math.max(activeUntil, performance.now() + ms));
  const frameTimes = new URLSearchParams(location.search).has('perf') ? (window.frameTimes = []) : null;
  function loop(now) {
    requestAnimationFrame(loop);
    // The reading view covers the map: leave it be until the view closes.
    if (state.reading) return;
    const busy = anim || drag || pinch || now < activeUntil;
    if (now - prev < 1000 / (busy ? 60 : 30) - 4) return;
    const started = performance.now();
    const dt = Math.min(100, now - prev);
    prev = now;
    if (stepLayout()) keepBusy();
    stepView(now, dt);
    if (stepHighlights(dt)) mapDirty = true;
    const vw = view();
    const p = project(vw);
    const fr = frame(vw);
    const key = [vw.z, v.lon, v.lat, lay.cx, lay.cy, lay.sg, lay.sf].map((n) => n.toFixed(3)).join();
    // The backdrop doesn't change as the globe turns: only the zoom, the layout (and, on the map, the latitude) move it.
    const backKey = [vw.z, vw.a > 0 ? vw.yF : 0, lay.cx, lay.cy, lay.sg, lay.sf].map((n) => n.toFixed(3)).join();
    if (backKey !== backdropKey || mapDirty) {
      backdropKey = backKey;
      drawBackdrop(backLayer, { vw, projection: p, frame: fr });
    }
    if (key !== mapKey || mapDirty) {
      mapKey = key;
      mapDirty = false;
      const flat = flatAt(vw);
      drawMap(mapLayer, { vw, projection: p, frame: fr, land, borders, coast, graticule, highlights, detail: flat ? detail : null, flat });
    }
    if (!state.sel) arcs = null;
    else if (arcsKey !== `${key}|${state.sel.id}`) {
      arcsKey = `${key}|${state.sel.id}`;
      arcs = constellation(vw, p);
    }

    const t = reduceMotion ? 0 : (now - t0) / 1000;
    onScreen = placeWisps(vw, p, fr, t);
    wispLayer.clear();
    const ctx = wispLayer.ctx;
    ctx.save();
    ctx.beginPath();
    ctx.ellipse(fr.cx, fr.cy, fr.rx, fr.ry, 0, 0, Math.PI * 2);
    ctx.clip();
    drawConstellation(ctx, sprites, arcs);
    ctx.restore();
    // (By the globe's size as laid out, not as zoomed: zooming in spreads the wisps, it doesn't swell them.)
    drawWisps(ctx, sprites, onScreen, t, clamp(Math.sqrt(lay.sg / 280), 0.65, 1));
    drawLabels();
    updateWispList(now);
    frameTimes?.push(performance.now() - started);

    const ck = `${Math.round((vw.z / ZMAX) * 100)}|${vw.z < 0.05}|${Math.abs(vw.z - flatZ()) < 0.05}`;
    if (ck !== controlsKey) {
      controlsKey = ck;
      $('#zoom-level').style.width = `${Math.round((vw.z / ZMAX) * 100)}%`;
      $('#to-globe').setAttribute('aria-pressed', vw.z < 0.05);
      $('#to-flat').setAttribute('aria-pressed', Math.abs(vw.z - flatZ()) < 0.05);
    }
  }

  // ---------- Turning and zooming ----------

  const pointers = new Map();
  const spread = () => {
    const [a, b] = [...pointers.values()];
    return Math.hypot(a.x - b.x, a.y - b.y) || 1;
  };
  const startDrag = ({ x, y }) => (drag = { x, y, lon: v.lon, lat: v.lat });

  // A tap or click that hardly moved opens the wisp under it.
  let press = null;
  worldEl.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    worldEl.setPointerCapture(e.pointerId);
    press = pointers.size === 0 ? { x: e.clientX, y: e.clientY, type: e.pointerType } : null;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    // A touch stops a flight, but not the globe unrolling or rolling up: that always finishes.
    if (!anim?.morph) anim = null;
    idleSince = performance.now();
    if (pointers.size === 1) startDrag(pointers.get(e.pointerId));
    else if (pointers.size === 2) {
      drag = null;
      pinch = { d: spread() };
    }
    worldEl.classList.add('dragging');
  });
  worldEl.addEventListener('pointermove', (e) => {
    if (!pointers.has(e.pointerId)) {
      if (e.pointerType === 'mouse') {
        const h = wispAt(e.clientX, e.clientY)?.tale ?? null;
        if (h !== hovered) keepBusy(300);
        hovered = h;
        worldEl.classList.toggle('pointing', !!hovered);
      }
      return;
    }
    if (press && Math.hypot(e.clientX - press.x, e.clientY - press.y) > 6) press = null;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    idleSince = performance.now();
    keepBusy();
    if (pinch && pointers.size >= 2) {
      const d = spread();
      zoomInput(Math.log2(d / pinch.d));
      pinch.d = d;
    } else if (anim?.morph) {
      if (drag) startDrag(pointers.get(e.pointerId));
    } else if (drag) {
      // In y rather than latitude, so the map stays under the finger on Mercator too.
      const { s, m } = view();
      const k = 1 / (RAD * s);
      v.lon = wrap(drag.lon - (e.clientX - drag.x) * k);
      v.lat = latOf(yOf(drag.lat, m) + (e.clientY - drag.y) * k, m);
      clampLat();
    }
  });
  const endPointer = (e) => {
    if (!pointers.delete(e.pointerId)) return;
    if (press && e.type === 'pointerup' && pointers.size === 0) {
      const w = wispAt(press.x, press.y, press.type === 'mouse' ? 20 : 28);
      if (w) pick(w.tale.id);
    }
    press = null;
    idleSince = performance.now();
    if (pointers.size < 2) pinch = null;
    if (pointers.size === 1) startDrag([...pointers.values()][0]);
    if (pointers.size === 0) {
      drag = null;
      worldEl.classList.remove('dragging');
    }
  };
  worldEl.addEventListener('pointerup', endPointer);
  worldEl.addEventListener('pointercancel', endPointer);
  worldEl.addEventListener('pointerleave', () => {
    hovered = null;
    worldEl.classList.remove('pointing');
  });

  worldEl.addEventListener(
    'wheel',
    (e) => {
      e.preventDefault();
      idleSince = performance.now();
      keepBusy();
      const dy = e.deltaMode === 1 ? e.deltaY * 33 : e.deltaY;
      zoomInput(-dy * 0.0015);
    },
    { passive: false },
  );

  // There is no stopping between the globe (z = 0) and the flat map (z = 1): zoom in on the globe, or out
  // past the flat map, and it unrolls or rolls up the whole way. One scroll or pinch carries on through:
  // zooming in on the globe unrolls it and then zooms into the map; zooming out of the map rolls it up
  // into the globe. While it unrolls or rolls up, the scroll or pinch is let go.
  const MORPH_PUSH = 0.1;
  let push = 0;
  let pushAt = 0;
  const toGlobe = (dur = 1100) => {
    idleSince = performance.now() - 3000;
    flyTo({ z: 0, lat: 22, morph: true }, dur);
    push = 0;
  };
  // Unrolled by zooming, the map keeps the latitude in view for zooming in further.
  // The flat map: z = 1, or on a phone or a portrait screen a little closer in, where it fills the screen.
  const flatZ = () =>
    fillQuery.matches ? Math.max(1.7, 1 + Math.log2((1.15 * Math.max(target.H / Math.PI, target.W / (2 * Math.PI))) / target.sf)) : 1;
  const toFlat = (dur = 1100, lat) => {
    flyTo({ z: flatZ(), lat, morph: true }, dur);
    push = 0;
  };
  // Turned upright on the flat map, move on to the one that fills the screen rather than stay half unrolled.
  fillQuery.addEventListener('change', () => {
    if (fillQuery.matches && !anim && v.z >= 1 && v.z < flatZ()) toFlat(600);
  });
  function zoomInput(dz) {
    const now = performance.now();
    const pause = now - pushAt > 250;
    pushAt = now;
    if (pause) push = 0;
    if (anim?.morph) return;
    anim = null;
    if (v.z < 1) {
      push = dz > 0 ? push + dz : 0;
      if (push > MORPH_PUSH) toFlat();
      return;
    }
    const flat = flatZ();
    const z = v.z + dz;
    if (z > flat) {
      v.z = Math.min(z, ZMAX);
      push = 0;
    } else {
      // Only what goes past the flat map pushes towards the globe.
      push += Math.min(v.z, flat) - z;
      v.z = flat;
      if (push > MORPH_PUSH) return toGlobe();
    }
    clampLat();
  }

  const zoomBy = (dz) => {
    idleSince = performance.now();
    const z = anim ? anim.to.z : v.z;
    const flat = flatZ();
    if (z < 1) {
      if (dz > 0) toFlat();
    } else if (z + dz >= flat) flyTo({ z: Math.min(z + dz, ZMAX) }, 500);
    else if (z > flat + 0.001) flyTo({ z: flat }, 500);
    else toGlobe();
  };
  worldEl.addEventListener('keydown', (e) => {
    const step = 12 / Math.pow(2, Math.max(0, v.z - 1));
    const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] };
    if (moves[e.key]) {
      idleSince = performance.now();
      const { m } = view();
      flyTo({ lon: v.lon + moves[e.key][0], lat: clamp(latOf(yOf(v.lat, m) + moves[e.key][1], m), -80, 80) }, 300);
    } else if (e.key === '+' || e.key === '=') zoomBy(0.7);
    else if (e.key === '-' || e.key === '_') zoomBy(-0.7);
    else return;
    e.preventDefault();
  });

  $('#zoom-in').addEventListener('click', () => zoomBy(0.7));
  $('#zoom-out').addEventListener('click', () => zoomBy(-0.7));
  $('#to-globe').addEventListener('click', () => toGlobe(1400));
  $('#to-flat').addEventListener('click', () => toFlat(1200, 0));
  // On a phone there is only room to say what the box is for.
  const placeholder = () => ($('#search').placeholder = sheetQuery.matches ? 'Search' : 'Search a tale, a type, a land');
  placeholder();
  sheetQuery.addEventListener('change', placeholder);

  // ---------- The chosen tale ----------

  // sel: the chosen tale; list: the place whose tales the panel lists ("All 229 tales from Germany"), kept
  // while a tale from the list is open so the panel can go back to it.
  const state = { sel: null, list: null, listScroll: 0, kin: [], fam: [], reading: false, allKin: false, allFam: false, collapsed: false };
  const taleCache = new Map();
  const loadTale = (id) => {
    if (!taleCache.has(id)) taleCache.set(id, json(`data/tales/${id}.json`).catch((err) => (taleCache.delete(id), Promise.reject(err))));
    return taleCache.get(id);
  };

  function pick(id, { fly = true, reading = false } = {}) {
    const tale = taleById.get(Number(id));
    if (!tale) return false;
    const same = state.sel === tale;
    const wasReading = state.reading;
    state.sel = tale;
    state.reading = reading;
    if (!same) state.allKin = state.allFam = false;
    state.collapsed = false;
    select();
    if (!same) panel.scrollTop = 0;
    if (reading && (!same || !wasReading)) renderReader(tale);
    if (!reading && wasReading) closeReaderView();
    if (fly) {
      idleSince = performance.now();
      const { lon, lat } = tale;
      if (v.z < 0.7) flyTo({ lon, lat: clamp(lat, -60, 60) }, 1100);
      else if (v.z > 1.4) flyTo({ lon, lat }, 800);
    }
    return true;
  }

  function unselect() {
    state.sel = null;
    state.reading = false;
    state.kin = [];
    state.fam = [];
    arcsKey = '';
    forced = new Map();
    markWisps();
  }

  // Let go of the chosen tale and the list: the panel closes and the globe may turn on its own again.
  function deselect() {
    if (!state.sel && !state.list) return;
    const wasIn = state.reading || panel.contains(document.activeElement);
    if (state.reading) closeReaderView();
    unselect();
    state.list = null;
    closeAtu();
    setHighlights(null, [], []);
    panel.hidden = true;
    panel.classList.remove('full');
    panel.style.maxHeight = panel.style.transform = '';
    idleSince = performance.now();
    if (location.hash) history.replaceState(null, '', location.pathname + location.search);
    if (wasIn) worldEl.focus({ preventScroll: true });
  }

  // Light up the chosen tale, its kin and their countries, then fill in the panel.
  function select() {
    const sel = state.sel;
    state.kin = kinOf(sel, 'type');
    state.fam = kinOf(sel, 'family', state.kin);
    forced = new Map([...state.fam.map((k) => [k.tales[0], 'sib']), ...state.kin.map((k) => [k.tales[0], 'kin']), [sel, 'on']]);
    markWisps();
    setHighlights(
      sel.place.country,
      state.kin.map((k) => k.place.country),
      state.fam.map((k) => k.place.country),
    );
    renderPanel();
    const hash = state.reading ? `#read/${sel.id}` : `#${sel.place.id}/${sel.id}`;
    if (location.hash !== hash) history.replaceState(null, '', hash);
  }

  const nPlaces = (n) => `${n} ${n === 1 ? 'place' : 'places'}`;
  const kinHead = (n) => (n ? `Similar tales are told in ${nPlaces(n)}` : 'Similar tales are not recorded elsewhere yet');
  const famHead = (n) => (n ? 'Tales from the same family' : 'No other places have tales from the same family yet');
  const nTales = (n) => `${n} ${n === 1 ? 'tale' : 'tales'}`;
  const range = ([from, to]) => `ATU ${from}–${to}`;
  const named = (t) => t.typeName && t.typeName !== 'Unnamed tale type';

  function renderPanel() {
    const sel = state.sel;
    panel.hidden = false;
    panel.classList.toggle('listing', !sel);
    panel.classList.toggle('collapsed', state.collapsed);
    const toggle = $('#sheet-toggle');
    toggle.setAttribute('aria-expanded', !state.collapsed);
    toggle.setAttribute('aria-label', state.collapsed ? 'Show more of this tale' : 'Show less of this tale');
    $('#to-list').hidden = !(sel && state.list);
    if (state.list) $('#to-list').setAttribute('aria-label', `Back to all tales from ${state.list.name}`);
    $('#close').setAttribute('aria-label', sel ? 'Close this tale' : 'Close the list');
    if (!sel) return renderList(state.list);

    $('#tale-place').textContent = sel.place.name;
    $('#tale-title').textContent = sel.title;
    renderType($('#tale-type'), sel);
    $('#tale-excerpt').textContent = sel.excerpt;
    const nKin = state.kin.length;
    const nFam = state.fam.length;
    $('#kin-head').textContent = kinHead(nKin);
    $('#fam-head').textContent = famHead(nFam);
    setTips(sel, $('#kin-tip'), $('#fam-tip'));
    renderChips($('#kin-chips'), state.kin, '', state.allKin ? Infinity : KIN_SHOWN, () => ((state.allKin = true), renderPanel()));
    renderChips($('#fam-chips'), state.fam, 'silver', state.allFam ? Infinity : FAM_SHOWN, () => ((state.allFam = true), renderPanel()));
    renderOthers(sel);
  }

  // The tale's type, "Cinderella (ATU 510A)", over its kind and family, "Tales of Magic › Supernatural
  // Helpers": the code, the kind and the family each open the index of tale types there.
  function renderType(box, t) {
    const name = el('div', 'tt-name');
    if (named(t)) name.append(`${t.typeName} `);
    name.append(atuLink(named(t) ? `(ATU ${t.atu})` : `ATU ${t.atu}`, { type: t.atu }, `About tale type ATU ${t.atu}`));
    const path = el('div', 'tt-path');
    if (t.kind) path.append(atuLink(t.kind, { kind: t.kindIdx }));
    if (t.family) path.append(el('span', 'sep', '›'), atuLink(t.family, { family: t.famIdx }));
    box.replaceChildren(name, path);
  }

  // How "similar tale" and "similar family" are found, for the "?" next to each.
  function setTips(t, kinTip, famTip) {
    const type = named(t) ? `${t.typeName}, ATU ${t.atu}` : `ATU ${t.atu}`;
    kinTip.textContent = `Tales of the same tale type (${type}) in the Aarne–Thompson–Uther index of folktales.`;
    const fam = data.families[t.famIdx];
    famTip.textContent = fam
      ? `Tales of other types in the same family of the index (${fam[0]}, ${range(fam.slice(2))}).`
      : 'This tale type has no family in the index.';
  }

  // A chip for every place in kin, up to max, and a "+ n more" chip that shows the rest.
  function renderChips(box, kin, cls, max, showAll, opts) {
    box.replaceChildren();
    for (const { place, tales: list } of kin.slice(0, max)) {
      const b = el('button', `kc ${cls}`.trim(), place.name);
      b.title = list.length === 1 ? `Open “${list[0].title}”` : `${list.length} tales; open “${list[0].title}”`;
      b.addEventListener('click', () => pick(list[0].id, opts));
      box.append(b);
    }
    if (kin.length > max) {
      const more = el('button', 'kc more-kin', `+ ${kin.length - max} more`);
      more.addEventListener('click', showAll);
      box.append(more);
    }
  }

  // A "?" shows its tip below it, or above it near the bottom of the screen, over the panel's edges.
  for (const q of document.querySelectorAll('.q')) {
    const place = () => {
      const tip = q.nextElementSibling;
      const r = q.getBoundingClientRect();
      const below = r.bottom + 170 < innerHeight;
      tip.style.left = `${clamp(r.left - 20, 8, innerWidth - 308)}px`;
      tip.style.top = below ? `${r.bottom + 8}px` : 'auto';
      tip.style.bottom = below ? 'auto' : `${innerHeight - r.top + 8}px`;
    };
    q.addEventListener('pointerenter', place);
    q.addEventListener('focus', place);
  }

  function otherRow(t) {
    const b = el('button', 'more', t.title);
    b.addEventListener('click', () => pick(t.id));
    return b;
  }

  function renderOthers(sel) {
    const others = sel.place.tales.filter((t) => t !== sel);
    $('#others').hidden = !others.length;
    $('#others-head').textContent = `More tales from ${sel.place.name}`;
    const list = $('#others-list');
    list.replaceChildren(...others.slice(0, OTHERS_SHOWN).map((t) => otherRow(t)));
    if (others.length > OTHERS_SHOWN) {
      const all = el('button', 'more all', `All ${others.length + 1} tales from ${sel.place.name}`);
      all.addEventListener('click', () => openList(sel.place));
      list.append(all);
    }
  }

  // ---------- All the tales of a place ----------

  // The panel lists every tale of a place, and the map flies to the place's tales.
  function openList(place, { fly = true } = {}) {
    const back = state.list === place && state.sel;
    if (state.reading) closeReaderView();
    unselect();
    state.list = place;
    state.collapsed = false;
    closeAtu();
    setHighlights(place.country, [], []);
    renderPanel();
    panel.scrollTop = back ? state.listScroll : 0;
    const hash = `#${place.id}/all`;
    if (location.hash !== hash) history.replaceState(null, '', hash);
    if (fly) flyToPlace(place);
    if (back || panel.contains(document.activeElement) || document.activeElement === document.body) $('#tale-title').focus({ preventScroll: true });
  }

  function renderList(place) {
    $('#tale-place').textContent = `All ${nTales(place.tales.length)} from`;
    $('#tale-title').textContent = place.name;
    const sorted = [...place.tales].sort((a, b) => a.title.localeCompare(b.title));
    $('#list').replaceChildren(
      ...sorted.map((t) => {
        const b = el('button', 'more', t.title);
        b.addEventListener('click', () => {
          state.listScroll = panel.scrollTop;
          pick(t.id, { fly: false });
          $('#to-list').focus({ preventScroll: true });
        });
        return b;
      }),
    );
  }

  // Zoom the map so the place's tales fill the part of the screen the panel leaves free, centred there.
  function flyToPlace(place) {
    let [x0, x1, y0, y1] = [Infinity, -Infinity, Infinity, -Infinity];
    for (const t of place.tales) {
      const dx = wrap(t.lon - place.lng);
      (x0 = Math.min(x0, dx)), (x1 = Math.max(x1, dx)), (y0 = Math.min(y0, t.lat)), (y1 = Math.max(y1, t.lat));
    }
    const lon = place.lng + (x0 + x1) / 2;
    const lat = (y0 + y1) / 2;
    const r = panel.getBoundingClientRect();
    const sheet = sheetQuery.matches;
    const freeW = sheet ? lay.W : r.left;
    const freeH = sheet ? r.top - 120 : lay.H - 200;
    // How tall the tales stand depends on how far the map has turned Mercator at the zoom that fits
    // them: fit as if all the way, then again at the zoom that gives.
    const fit = (m) => {
      const h = Math.max(5 / Math.cos(clamp(lat, -80, 80) * RAD) ** m, yOf(y1, m) - yOf(y0, m));
      const k = Math.min((freeW * 0.6) / (Math.max(8, x1 - x0) * RAD * lay.sf), (freeH * 0.6) / (h * RAD * lay.sf));
      return clamp(1 + Math.log2(k), Math.max(1.3, flatZ()), ZMAX);
    };
    const z = fit(mercatorAt(fit(1)));
    const s = lay.sf * Math.pow(2, z - 1);
    idleSince = performance.now();
    flyTo({ z, lon: lon - (freeW / 2 - lay.cx) / (RAD * s), lat }, 1200);
  }

  $('#to-list').addEventListener('click', () => state.list && openList(state.list));

  // ---------- The index of tale types: kinds, families, types and their tales ----------

  const atuPop = $('#atu');
  const atuScrim = $('#atu-scrim');
  const typesOf = new Map(); // family index -> atu ids, by number
  for (const atu of Object.keys(data.types).sort((a, b) => parseInt(a, 10) - parseInt(b, 10) || a.localeCompare(b))) {
    const f = data.types[atu][1];
    if (!typesOf.has(f)) typesOf.set(f, []);
    typesOf.get(f).push(atu);
  }
  const talesOfType = (atu) => [...(byType.get(atu)?.values() ?? [])].flat();
  const talesOfFamily = (f) => [...(byFamily.get(f)?.values() ?? [])].flat().length;
  const talesOfKind = (k) => data.families.reduce((n, f, i) => n + (f[1] === k ? talesOfFamily(i) : 0), 0);
  let summaries = null;
  const loadSummaries = () => (summaries ??= json('data/types.json').catch((err) => ((summaries = null), Promise.reject(err))));
  let atuAnchor = null;

  function atuLink(text, node, label) {
    const b = el('button', 'atu-link', text);
    b.type = 'button';
    b.setAttribute('aria-haspopup', 'dialog');
    if (label) b.setAttribute('aria-label', label);
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      if (atuAnchor === b && !atuPop.hidden) return closeAtu(true);
      openAtu(node, b);
    });
    return b;
  }

  function openAtu(node, anchor) {
    atuAnchor = anchor;
    atuPop.hidden = atuScrim.hidden = false;
    renderAtu(node);
    $('#atu-title').focus({ preventScroll: true });
  }

  function closeAtu(refocus = false) {
    if (atuPop.hidden) return;
    atuPop.hidden = atuScrim.hidden = true;
    if (refocus && atuAnchor?.isConnected) atuAnchor.focus({ preventScroll: true });
    atuAnchor = null;
  }

  // node: {} (all kinds), { kind }, { family } or { type }
  function renderAtu(node) {
    const crumbs = [];
    const crumb = (text, to) => crumbs.push([text, to]);
    const row = (title, meta, onClick, current) => {
      const b = el('button', 'more', title);
      if (meta) b.append(el('span', '', meta));
      if (current) b.setAttribute('aria-current', 'true');
      b.addEventListener('click', onClick);
      return b;
    };
    const go = (to) => () => renderAtu(to);
    const body = [];
    let title;
    let sub;
    crumb('Tale types', {});
    if (node.type != null) {
      const [name, f] = data.types[node.type];
      const fam = data.families[f];
      if (fam) crumb(data.kinds[fam[1]], { kind: fam[1] }), crumb(fam[0], { family: f });
      title = name && name !== 'Unnamed tale type' ? name : `ATU ${node.type}`;
      const told = talesOfType(node.type);
      const nPl = byType.get(node.type)?.size ?? 0;
      sub = `ATU ${node.type} · ${nTales(told.length)} in ${nPlaces(nPl)}`;
      const summary = el('p', 'atu-summary', '…');
      body.push(summary);
      loadSummaries()
        .then((all) => (summary.textContent = all[node.type] ?? 'The index has no description of this type.'))
        .catch(() => (summary.textContent = 'The description could not be loaded.'));
      body.push(el('div', 'kh', 'Tales of this type'));
      for (const t of told.sort((a, b) => a.place.name.localeCompare(b.place.name) || a.title.localeCompare(b.title))) {
        body.push(
          row(t.title, t.place.name, () => (closeAtu(), pick(t.id, { reading: state.reading })), t === state.sel),
        );
      }
    } else if (node.family != null) {
      const [name, k, from, to] = data.families[node.family];
      crumb(data.kinds[k], { kind: k });
      title = name;
      sub = `${range([from, to])} · ${nTales(talesOfFamily(node.family))}`;
      for (const atu of typesOf.get(node.family) ?? []) {
        const n = talesOfType(atu).length;
        const [tn] = data.types[atu];
        body.push(row(tn && tn !== 'Unnamed tale type' ? tn : `ATU ${atu}`, `ATU ${atu} · ${n}`, go({ type: atu }), state.sel?.atu === atu));
      }
    } else if (node.kind != null) {
      title = data.kinds[node.kind];
      sub = `${range(data.kindRanges[node.kind])} · ${nTales(talesOfKind(node.kind))}`;
      let empty = 0;
      data.families.forEach(([name, k, from, to], i) => {
        if (k !== node.kind) return;
        const n = talesOfFamily(i);
        if (!n) return empty++;
        body.push(row(name, `${range([from, to])} · ${n}`, go({ family: i }), state.sel?.famIdx === i));
      });
      if (empty) body.push(el('p', 'atu-note', `${empty} ${empty === 1 ? 'family' : 'families'} of this kind ${empty === 1 ? 'has' : 'have'} no tales here yet.`));
    } else {
      title = 'The kinds of tale';
      sub = 'The Aarne–Thompson–Uther (ATU) index sorts folktales into types, the types into families and the families into kinds.';
      data.kinds.forEach((name, k) => body.push(row(name, `${range(data.kindRanges[k])} · ${talesOfKind(k)}`, go({ kind: k }), state.sel?.kindIdx === k)));
    }
    const root = node.type == null && node.family == null && node.kind == null;
    if (root) crumbs.pop();
    const nav = $('#atu-crumbs');
    nav.replaceChildren();
    for (const [text, to] of crumbs) {
      const b = el('button', 'crumb', text);
      b.addEventListener('click', go(to));
      nav.append(b, el('span', 'sep', '›'));
    }
    nav.append(el('span', 'crumb here', node.type != null ? `ATU ${node.type}` : root ? 'Tale types' : title));
    $('#atu-title').textContent = title;
    $('#atu-sub').textContent = sub;
    $('#atu-body').replaceChildren(...body);
    $('#atu-body').scrollTop = 0;
  }

  atuPop.addEventListener('click', (e) => e.stopPropagation());
  document.addEventListener('pointerdown', (e) => {
    if (!atuPop.hidden && !atuPop.contains(e.target) && e.target !== atuAnchor) closeAtu();
  });
  $('#atu-close').addEventListener('click', () => closeAtu(true));

  $('#read').addEventListener('click', () => pick(state.sel.id, { fly: false, reading: true }));
  $('#close').addEventListener('click', deselect);
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || e.target === search) return;
    if (!atuPop.hidden) return closeAtu(true);
    if (state.reading) closeReader();
    else if (state.sel && state.list) openList(state.list);
    else deselect();
  });
  let dragged = false;
  $('#sheet-toggle').addEventListener('click', () => {
    if (dragged) return void (dragged = false);
    state.collapsed = !state.collapsed;
    renderPanel();
  });

  // On a phone the sheet follows a finger on its top edge: pulled down it slides away and closes,
  // pulled up it grows to fill the screen. Let go, and it settles at its usual height or the full one.
  let sheetDrag = null;
  // The tallest the sheet grows (as #panel.full): 40px short of the screen's top, below a notch.
  const insetProbe = document.body.appendChild(el('div', 'inset-probe'));
  const sheetMax = () => innerHeight - 40 - insetProbe.offsetHeight;
  const sheetGrip = (e) => {
    if (!sheetQuery.matches || e.target.closest('#close, #to-list') || (e.pointerType === 'mouse' && e.button !== 0)) return;
    dragged = false;
    sheetDrag = { id: e.pointerId, y: e.clientY, h: panel.offsetHeight, moved: false, at: e.timeStamp, last: e.clientY, speed: 0 };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  // How far the sheet is pulled: its height, and how far it has slid down below its usual height (or below
  // the height it had, if smaller).
  const sheetPull = (y) => {
    const h = sheetDrag.h - (y - sheetDrag.y);
    const base = Math.min(sheetDrag.h, innerHeight * 0.46);
    return { h: clamp(Math.max(h, base), 0, sheetMax()), down: Math.max(0, base - h) };
  };
  const sheetMove = (e) => {
    if (sheetDrag?.id !== e.pointerId) return;
    const dy = e.clientY - sheetDrag.y;
    if (!sheetDrag.moved) {
      if (Math.abs(dy) < 6) return;
      sheetDrag.moved = dragged = true;
      panel.classList.add('dragging');
      if (state.collapsed && dy < 0) (state.collapsed = false), renderPanel();
    }
    const dt = e.timeStamp - sheetDrag.at;
    if (dt > 0) sheetDrag.speed = 0.6 * sheetDrag.speed + (0.4 * (e.clientY - sheetDrag.last)) / dt;
    sheetDrag.at = e.timeStamp;
    sheetDrag.last = e.clientY;
    const { h, down } = sheetPull(e.clientY);
    panel.style.maxHeight = `${h}px`;
    panel.style.transform = down ? `translateY(${down}px)` : '';
  };
  const sheetRelease = (e) => {
    if (sheetDrag?.id !== e.pointerId) return;
    const { moved, speed } = sheetDrag;
    const { h, down } = sheetPull(e.clientY);
    const base = Math.min(sheetDrag.h, innerHeight * 0.46);
    sheetDrag = null;
    panel.classList.remove('dragging');
    if (!moved) return;
    panel.classList.add('settling');
    setTimeout(() => panel.classList.remove('settling'), 300);
    if (e.type === 'pointercancel') return void (panel.style.maxHeight = panel.style.transform = '');
    if (down > Math.min(120, base * 0.35) || (down > 16 && speed > 0.5)) {
      panel.style.transform = `translateY(${panel.offsetHeight + 24}px)`;
      setTimeout(deselect, 220);
      return;
    }
    const full = speed < -0.5 || (speed < 0.5 && h > (base + sheetMax()) / 2);
    panel.classList.toggle('full', full);
    panel.style.maxHeight = panel.style.transform = '';
  };
  for (const grip of [$('#sheet-toggle'), $('#panel > .p-head')]) {
    grip.addEventListener('pointerdown', sheetGrip);
    grip.addEventListener('pointermove', sheetMove);
    grip.addEventListener('pointerup', sheetRelease);
    grip.addEventListener('pointercancel', sheetRelease);
  }

  // ---------- The reading view ----------

  const reader = $('#reader');
  const readerScroll = $('#r-scroll');
  const readerBar = $('#r-bar');
  const readingEl = $('#reading');

  async function renderReader(sel) {
    document.body.classList.add('reading');
    reader.hidden = false;
    readerScroll.scrollTop = 0;
    readerBar.classList.remove('shown');
    $('#r-place').textContent = sel.place.name;
    $('#r-title').textContent = sel.title;
    $('#r-mini-title').textContent = sel.title;
    $('#r-mini-place').textContent = sel.place.name;
    renderType($('#r-type'), sel);
    $('#r-source').replaceChildren();
    $('#r-kin').hidden = !state.kin.length;
    $('#r-kin-head').textContent = kinHead(state.kin.length);
    renderChips($('#r-kin-chips'), state.kin, '', Infinity, null, { reading: true });
    $('#r-fam').hidden = !state.fam.length;
    $('#r-fam-head').textContent = famHead(state.fam.length);
    renderChips($('#r-fam-chips'), state.fam, 'silver', Infinity, null, { reading: true });
    setTips(sel, $('#r-kin-tip'), $('#r-fam-tip'));
    readingEl.replaceChildren(firstParagraph(sel.excerpt));
    readingEl.focus({ preventScroll: true });

    let tale;
    try {
      tale = await loadTale(sel.id);
    } catch {
      readingEl.append(el('p', 'r-source', 'The full tale could not be loaded. Try again later.'));
      return;
    }
    if (state.sel !== sel || !state.reading) return;
    const [first = '', ...rest] = tale.paragraphs;
    readingEl.replaceChildren(firstParagraph(first), ...rest.map((p) => el('p', '', p)));
    const link = (href, text) => Object.assign(el('a', '', text), { href, target: '_blank', rel: 'noopener' });
    $('#r-source').replaceChildren(
      ...(tale.source ? [`Source: ${tale.source}`, el('br')] : []),
      'From D. L. Ashliman’s ',
      link('https://sites.pitt.edu/~dash/folktexts.html', 'Folktexts'),
      ', via the Annotated Folktales in ',
      link('https://github.com/j-hagedorn/trilogy', 'trilogy'),
      ' (CC BY-SA 4.0).',
    );
  }

  // The first paragraph opens with its first letter as a framed initial (leading quotes dropped).
  function firstParagraph(text) {
    const p = el('p');
    const lead = text.match(/^["'‘“(]*/)[0].length;
    const letter = text.charAt(lead).toUpperCase();
    if (!letter) return p;
    const cap = el('span', 'cap');
    cap.setAttribute('aria-hidden', 'true');
    cap.append(el('i'), el('i'), el('i'), el('i'), el('b', '', letter));
    p.append(cap, el('span', 'sr-only', letter), text.slice(lead + 1));
    return p;
  }

  function closeReaderView() {
    document.body.classList.remove('reading');
    reader.hidden = true;
    mapDirty = true;
  }
  // Back to the atlas, with the tale still chosen.
  function closeReader() {
    if (!state.reading) return;
    pick(state.sel.id, { fly: false });
    $('#read').focus({ preventScroll: true });
  }
  $('#back').addEventListener('click', closeReader);
  $('#r-close').addEventListener('click', closeReader);
  $('#r-bar-close').addEventListener('click', closeReader);
  readerScroll.addEventListener(
    'scroll',
    () => {
      readerBar.classList.toggle('shown', readerScroll.scrollTop > 280);
      closeAtu();
    },
    { passive: true },
  );

  // Text size, remembered on this device.
  let fontStep = 2;
  try {
    const saved = localStorage.getItem('folk-atlas:text-size');
    if (saved !== null && Number(saved) in FONT_SIZES) fontStep = Number(saved);
  } catch {}
  function setFontStep(step) {
    fontStep = clamp(step, 0, FONT_SIZES.length - 1);
    readingEl.style.setProperty('--fs', `${FONT_SIZES[fontStep]}px`);
    $('#fs-down').disabled = fontStep === 0;
    $('#fs-up').disabled = fontStep === FONT_SIZES.length - 1;
  }
  const stepFont = (d) => {
    setFontStep(fontStep + d);
    try {
      localStorage.setItem('folk-atlas:text-size', String(fontStep));
    } catch {}
  };
  setFontStep(fontStep);
  $('#fs-down').addEventListener('click', () => stepFont(-1));
  $('#fs-up').addEventListener('click', () => stepFont(1));

  // ---------- Search ----------

  const search = $('#search');
  const results = $('#results');
  let found = [];
  let active = -1;

  function runSearch() {
    const matches = searchTales(tales, search.value);
    if (!matches) return closeResults();
    found = matches;
    active = found.length ? 0 : -1;
    results.replaceChildren();
    if (!found.length) results.append(el('li', 'r-none', 'No tale matches that yet.'));
    found.forEach((t, i) => {
      const li = el('li');
      li.id = `result-${t.id}`;
      li.setAttribute('role', 'option');
      li.append(el('span', 'r-title', t.title), el('span', 'r-meta', `${t.place.name} · ATU ${t.atu}${t.typeName ? ` · ${t.typeName}` : ''}`));
      li.addEventListener('mousedown', (e) => e.preventDefault());
      li.addEventListener('click', () => choose(i));
      results.append(li);
    });
    results.hidden = false;
    search.setAttribute('aria-expanded', 'true');
    highlight();
  }
  function highlight() {
    [...results.children].forEach((li, i) => li.setAttribute('aria-selected', i === active));
    const li = found[active] && $(`#result-${found[active].id}`);
    if (li) {
      search.setAttribute('aria-activedescendant', li.id);
      li.scrollIntoView({ block: 'nearest' });
    } else search.removeAttribute('aria-activedescendant');
  }
  function closeResults() {
    results.hidden = true;
    found = [];
    active = -1;
    search.setAttribute('aria-expanded', 'false');
    search.removeAttribute('aria-activedescendant');
  }
  function choose(i) {
    const t = found[i];
    if (!t) return;
    search.value = '';
    closeResults();
    pick(t.id);
  }
  search.addEventListener('input', runSearch);
  search.addEventListener('focus', runSearch);
  search.addEventListener('blur', closeResults);
  search.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!found.length) return;
      active = (active + (e.key === 'ArrowDown' ? 1 : -1) + found.length) % found.length;
      highlight();
    } else if (e.key === 'Enter') choose(Math.max(0, active));
    else if (e.key === 'Escape') {
      search.value = '';
      closeResults();
    } else return;
    e.preventDefault();
  });

  // ---------- Links: #germany, #germany/238, #germany/all, #read/238 ----------

  function fromHash() {
    const [a, b] = decodeURIComponent(location.hash.slice(1)).split('/');
    if (a === 'read' && b) return pick(b, { reading: true });
    const place = placeById.get(a);
    if (b === 'all') return place ? (openList(place), true) : false;
    if (b) return pick(b);
    return place ? pick(place.tales[0].id) : false;
  }
  window.addEventListener('hashchange', () => fromHash() || deselect());

  // ---------- Start ----------

  loading.hidden = true;
  measure();
  fromHash();
  measure();
  lay = { ...target };
  new ResizeObserver(measure).observe(panel);
  window.addEventListener('resize', onResize(measure));
  requestAnimationFrame(loop);
  document.fonts?.ready.then(() => (mapDirty = true));
  // Indexing the detailed outlines takes some 50 ms: do it when the page is idle.
  const idle = window.requestIdleCallback ?? ((fn) => setTimeout(fn, 200));
  json('data/world-detail.json')
    .then((topo) =>
      idle(
        () => {
          detail = detailMap(topo);
          mapDirty = true;
        },
        { timeout: 2000 },
      ),
    )
    .catch(console.error);

  // ---------- Background stars ----------

  function drawSky() {
    const sky = $('#sky');
    const W = innerWidth;
    const H = innerHeight;
    laidOut = { W, H };
    const rnd = random(42);
    const sizes = [0.5, 0.6, 0.8, 1, 1.2];
    const alphas = [0.2, 0.3, 0.45, 0.6];
    let dots = '';
    for (let i = Math.round((W * H) / 4800); i > 0; i--) {
      const x = (rnd() * W).toFixed(0);
      const y = (rnd() * H).toFixed(0);
      dots += `<circle cx="${x}" cy="${y}" r="${sizes[Math.floor(rnd() * 5)]}" opacity="${alphas[Math.floor(rnd() * 4)]}"></circle>`;
    }
    sky.setAttribute('viewBox', `0 0 ${W} ${H}`);
    sky.style.width = `${W}px`;
    sky.style.height = `${H}px`;
    sky.innerHTML = `<g fill="#DDE2FF">${dots}</g>`;
  }
})();
